import { Icons, Progress, useHelmet } from '@agro-club/agroclub-shared'
import { useFilters } from 'hooks/useFilter'
import { AuthContext } from 'modules/context/AuthContext'
import { useSingleEntity } from 'modules/domain/common/hooks'
import { ListRequestParams } from 'modules/domain/types'
import { endpoints } from 'modules/endpoints'
import { FC, useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AButton } from 'views/components/Analytics'
import APageWrapper from 'views/components/PageWrapper/APageWrapper'
import PermissionCheck from 'views/components/PermissionCheck/PermissionCheck'
import ATableFullPage from 'views/components/Table/ATableFullPage'
import { defaultListRequestParams } from 'views/components/TableFilters/data'
import { TaskModal } from 'views/components/TaskModal/TaskModal'
import * as Layout from 'views/layouts/NewLayout'
import TasksRoutes from 'views/pages/MyTasks/routes'
import { AnalyticsPages, AnalyticsPlaces } from '../analyticsPlaces'
import { User } from '../TaskDetail/types'
import { TaskDistributionModal } from './TaskDistribution/TaskDistributionModal'
import { tasksDefaultListFilter, TasksFilters } from './TaskFilters'
import useColumns from './useColumns'

export const TasksList: FC = () => {
  const { t } = useTranslation('my_tasks')
  useHelmet({ title: t('my_tasks') })
  const { profile: currentUser } = useContext(AuthContext)
  const [taskAssigneeProgress, taskAssignee] = useSingleEntity<User[]>(
    currentUser?.id && `${endpoints.taskAssignees()}?id=${currentUser?.id}`,
  )

  const [listRequestParams, setListRequestParams] = useState<ListRequestParams>({
    ...defaultListRequestParams,
  })
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
  const handlerModal = () => setIsTaskModalOpen((isOpen) => !isOpen)
  const [isDistributionModal, setIsDistributionModal] = useState(false)
  const handlerDistributionModal = () => setIsDistributionModal((isOpen) => !isOpen)
  const { isFiltersLoaded, updateFilterState } = useFilters({
    listRequestParams,
    setListRequestParams,
    defaultParams: tasksDefaultListFilter,
  })
  const [allStepsLoaded, setAllStepsLoaded] = useState(false)

  useEffect(() => {
    if (taskAssigneeProgress === Progress.SUCCESS && isFiltersLoaded) {
      if (taskAssignee?.length && !listRequestParams.filter.assignee) {
        updateFilterState({ assignee: taskAssignee[0].id })
      }
      setAllStepsLoaded(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskAssignee, isFiltersLoaded])

  const onTaskActionSuccess = useCallback(() => {
    setListRequestParams({ ...listRequestParams })
  }, [listRequestParams])

  const columns = useColumns(onTaskActionSuccess)

  return (
    <APageWrapper page={AnalyticsPages.TASKS} place={AnalyticsPlaces.LIST}>
      <Layout.WrapperContent>
        <Layout.Header>
          <Layout.TopHeader>
            <Layout.Breadcrumbs routes={[{ breadcrumbName: t('my_tasks'), path: TasksRoutes.List }]} />
            <Layout.HeaderWrapBtns>
              <PermissionCheck scope={['tasks.can_assign_tasks']}>
                <AButton id="distributeTasks" color="secondary" size="medium" onClick={handlerDistributionModal}>
                  {t('distribute_tasks')}
                </AButton>
              </PermissionCheck>
              <AButton id="add" color="approve" Icon={Icons.IconAdd} size="medium" onClick={handlerModal}>
                {t('add')}
              </AButton>
            </Layout.HeaderWrapBtns>
          </Layout.TopHeader>

          <Layout.PageName>{t('my_tasks')}</Layout.PageName>

          <TasksFilters
            setListRequestParams={setListRequestParams}
            updateFilterState={updateFilterState}
            listRequestParams={listRequestParams}
          />
        </Layout.Header>

        <ATableFullPage
          dataEndpoint={endpoints.tasksTableData()}
          listParamsUpdated={setListRequestParams}
          listRequestParams={listRequestParams}
          isFiltersLoaded={allStepsLoaded}
          columns={columns}
          rowHoverGray
        />
        {isDistributionModal && (
          <TaskDistributionModal
            refetch={() => setListRequestParams({ ...listRequestParams })}
            onClose={handlerDistributionModal}
          />
        )}

        {isTaskModalOpen && (
          <TaskModal
            onSuccess={() => setListRequestParams({ ...listRequestParams })}
            skipOnSuccessBeforeRedirect
            unclickableUserLink
            close={handlerModal}
            isMyTasks
          />
        )}
      </Layout.WrapperContent>
    </APageWrapper>
  )
}
